import { PlayerContextProvider, useMedia, usePlayerContext } from "./context.js";
import { useDestroy } from "../utils/use-destroy.js";
import { combinePlayerFeatureConfigs, setPlayerConfigValue } from "@videojs/core/dom";
import { combine, createStore } from "@videojs/store";
import { useStore } from "@videojs/store/react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { jsx } from "react/jsx-runtime";
import { pick } from "@videojs/utils/object";
//#region src/player/create-player.tsx
function createPlayer(config) {
	const slice = combine(...config.features);
	const featureConfig = combinePlayerFeatureConfigs(config.features);
	const configKeys = Object.keys(featureConfig);
	function createConfiguredStore(values) {
		const store = createStore()(slice);
		applyConfigValues(store, featureConfig, values);
		return store;
	}
	function Player(props) {
		const { children } = props;
		const configValues = pick(props, configKeys);
		const [store, setStore] = useState(() => createConfiguredStore(configValues));
		const syncedValues = useRef({
			store,
			values: configValues
		});
		const [media, setMedia] = useState(null);
		const [container, setContainer] = useState(null);
		useDestroy(store);
		useLayoutEffect(() => {
			const previous = syncedValues.current;
			if (previous.store !== store) {
				syncedValues.current = {
					store,
					values: configValues
				};
				return;
			}
			for (const key of configKeys) {
				if (Object.is(previous.values[key], configValues[key])) continue;
				setPlayerConfigValue(store, featureConfig[key], configValues[key]);
			}
			syncedValues.current = {
				store,
				values: configValues
			};
		});
		useEffect(() => {
			if (!media) return;
			if (store.destroyed) {
				setStore(createConfiguredStore(syncedValues.current.values));
				return;
			}
			return store.attach({
				media,
				container
			});
		}, [
			media,
			container,
			store
		]);
		const value = useMemo(() => ({
			store,
			media,
			setMedia,
			container,
			setContainer
		}), [
			store,
			media,
			container
		]);
		return /* @__PURE__ */ jsx(PlayerContextProvider, {
			value,
			children
		});
	}
	if (config.displayName) Player.displayName = config.displayName;
	function usePlayer(selector) {
		const { store } = usePlayerContext();
		return useStore(store, selector);
	}
	return {
		Player,
		usePlayer,
		useMedia
	};
}
function applyConfigValues(store, config, values) {
	for (const key of Object.keys(config)) setPlayerConfigValue(store, config[key], values[key]);
}
//#endregion
export { createPlayer };

//# sourceMappingURL=create-player.js.map